import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInsuranceState } from '../state/state';
import { __ } from '../functions/commom';
import { useParams } from 'react-router-dom';
import { ReactComponent as RightIcon } from '../assets/icons/right.svg';
import Button from './base/Button';
import { ComponentsMaker } from './componentsMaker';
import Box from './Box';
import { AGE_REQUIREMENTS, SOURCE_OF_BUSINESS_OTHER } from '../config';
import moment from 'moment-timezone';

const useStyles = createUseStyles({
  formHeader: {
    marginBottom: '30px',
  },
  formFooter: {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
  },
  header: {
    marginBottom: '30px',
  },
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '30px',
    borderLeft: '5px solid var(--dai-primary-color)',
    paddingLeft: '10px',
  },
});

const InsuranceFormStep1: React.FC = () => {
  const { id, category } = useParams<{ id?: string; category?: string }>();

  const classes = useStyles();
  const state = useInsuranceState();

  const [errors, setErrors] = useState<
    Array<{ field: string; message: string }>
  >([]);
  // const [showNoteModal, setShowNoteModal] = useState(false);

  const insuranceData = state.insurancePlans.get();
  const selectedInsurance = insuranceData?.find(
    (insurance) => insurance.id === id
  );

  const handleStep1Validation = () => {
    let errorsState: Array<{ field: string; message: string }> = [];

    // check all required fields
    selectedInsurance?.settings?.requiredHolderFields?.forEach((field) => {
      // Optional free-text phone number. Never block Next when it is blank.
      if (field === 'referralCode') {
        return;
      }

      // Source of business is optional, but picking "Others" means we still
      // need the free-text answer - that is the whole point of the field.
      if (field === 'sourceOfBusiness') {
        if (
          state.form.sourceOfBusiness.get() === SOURCE_OF_BUSINESS_OTHER &&
          !state.form.sourceOfBusinessOther.get()
        ) {
          errorsState.push({
            field: 'sourceOfBusiness',
            message: __('This field is required'),
          });
        }
        return;
      }

      // Type guard to ensure field is a valid key of the form state
      if (field in state.form) {
        const formField = field as keyof typeof state.form;
        if (!state.form[formField].get()) {
          errorsState.push({
            field: field,
            message: __('This field is required'),
          });
        }
      } else {
        errorsState.push({
          field: field,
          message: __('This field is required'),
        });
      }
    });

    // check dateOfBirth - policy holder must be 18 years old or above
    const dateOfBirth = state.form.dateOfBirth.get();
    if (dateOfBirth) {
      const birthDate = moment(dateOfBirth);
      const today = moment();
      // Calculate exact age in years using moment
      const minBirthDate = moment().subtract(AGE_REQUIREMENTS.adult.minAgeYears, 'years');

      // Check if birthdate is in the future
      if (birthDate.isAfter(today)) {
        errorsState.push({
          field: 'dateOfBirth',
          message: __('Date of Birth cannot be in the future'),
        });
      } else if (birthDate.isAfter(minBirthDate)) {
        // Policy holder must be at least 18 years old
        errorsState.push({
          field: 'dateOfBirth',
          message: __(
            'Must be at least $1 old',
            __(AGE_REQUIREMENTS.adult.minAgeDisplayKey)
          ),
        });
      }
    }

    // check email format
    const email = state.form.email.get();
    if (email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        errorsState.push({
          field: 'email',
          message: __('Please enter a valid email address'),
        });
      }
    }

    setErrors(errorsState);
    return errorsState.length === 0;
  };

  const handleNext = () => {
    if (handleStep1Validation()) {
      // setShowNoteModal(true);
      state.formStep.set(category === 'travel' ? 2 : 3);
    }
  };

  // const handleConfirmNote = () => {
  //   setShowNoteModal(false);
  //   state.formStep.set(category === 'travel' ? 2 : 3);
  // };

  const FormFooter: React.FC = () => {
    return (
      <div className={classes.formFooter}>
        <Button
          theme="primary"
          onClick={handleNext}
          icon={<RightIcon />}
          iconPosition="after"
        >
          {__('Next')}
        </Button>
      </div>
    );
  };

  useEffect(() => {
    state.topbar.title.set(__('Policyholder Information'));
  }, [state.topbar.title]);

  const holderFields = (
    selectedInsurance?.settings?.requiredHolderFields ?? []
  ).filter((field) => field !== 'referralCode');

  return (
    <>
      <Box header={__('Policyholder Information')} footer={<FormFooter />}>
        <div className={classes.formWrapper}>
          {holderFields.length > 0 &&
            holderFields.map((field) => (
              <ComponentsMaker
                key={field}
                component={field}
                errorMessage={
                  errors.find((error) => error.field === field)?.message
                }
              />
            ))}
          {selectedInsurance && (
            <ComponentsMaker
              component="referralCode"
              errorMessage={
                errors.find((error) => error.field === 'referralCode')?.message
              }
            />
          )}
        </div>
      </Box>

      {/* <Modal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        title={__('Important Notes to Proposer')}
        footer={
          <div style={{ display: 'flex', gap: '10px' }}>
             <Button
              theme="secondary"
              onClick={() => setShowNoteModal(false)}
            >
              {__('Cancel')}
            </Button>
            <Button
              theme="primary"
              onClick={handleConfirmNote}
            >
              {__('I Agree')}
            </Button>
          </div>
        }
      >
        <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
          <p>{__('IMPORTANT_NOTES_CONTENT_PLACEHOLDER')}</p>
          <ul>
            <li>{__('Note 1: Please declare all material facts...')}</li>
            <li>{__('Note 2: You must be a resident of Hong Kong...')}</li>
            <li>{__('Note 3: ...')}</li>
          </ul>
        </div>
      </Modal> */}
    </>
  );
};

export default InsuranceFormStep1;
